<template>
  <v-app id="app">
    <v-content>
      <mf-loading-dialog v-if="loading" loading />
      <v-container v-else class="pa-0" fluid>
        <transition-group name="slide-up">
          <div v-if="$isLogged()" key="page-template">
            <toolbar />
            <side-menu />
          </div>
          <div key="page">
            <router-view class="child-view" />
          </div>
        </transition-group>
      </v-container>
    </v-content>
  </v-app>
</template>

<script>
import { mapActions } from 'pinia'
import { useStore as mainUseStore } from '@/store'
import { getFingerprint } from '@/services/fingerprint.js'
import { formattedRedirectAfterLogin, formattedRedirectLogout } from '@/helpers/login'
import { QUERY_SINGLE_LOGIN, QUERY_REFRESH } from '@/modules/login/graphql'
import { QUERY_EXTERNAL_BI_MENUS } from '@/modules/external-bi/graphql'
import SideMenu from '@/components/SideMenu.vue'
import Toolbar from '@/components/Toolbar.vue'

export default {
  components: {
    SideMenu,
    Toolbar,
  },
  data: () => ({
    checkedToken: false,
    loading: false,
  }),
  watch: {
    $route(route) {
      if (route?.query?.auth_code) {
        this.removeAuthCodeFromURL()
      }
    },
  },
  async created() {
    this.loading = true
    try {
      if (this.$getSessionId()) {
        console.log('Checking refresh token')
        await this.verifyRefreshToken()
      } else {
        console.log('Checking auth code')
        await this.validateAuthcode()
      }
      await this.loadExternalBIMenus()
    } catch (error) {
      console.error('Error checking auth', error)
    } finally {
      this.loading = false
    }
  },
  methods: {
    ...mapActions(mainUseStore, ['setUserPermissions', 'setExternalBIMenus']),
    async loadExternalBIMenus() {
      try {
        const { data } = await this.$apollo.query({
          query: QUERY_EXTERNAL_BI_MENUS,
          fetchPolicy: 'no-cache',
          context: {
            uri: this.$microservicesUrls['externalBI'],
          },
        })
        this.setExternalBIMenus(data.externalBIMenus)
      } catch (error) {
        this.$snackbar({ message: 'Erro ao carregar menus.', snackbarColor: 'error' })
      }
    },
    async validateAuthcode() {
      this.checkedToken = false

      try {
        const authCode = this.getAuthCodeFromURL()
        let hash = window.localStorage.getItem('redirectAfterLogin')

        if (!authCode) {
          const singleLoginURL = this.$microservicesUrls['mfLogin']
          const host = this.getHost()
          hash = hash || window.location.hash.slice(1) || ''
          window.localStorage.setItem('redirectAfterLogin', hash)
          window.location.href = formattedRedirectAfterLogin(singleLoginURL, host, hash)
        }

        this.$setAuthCode(authCode)

        const {
          data: {
            loginWithAuthCode: { session_id, user },
          },
        } = await this.$apollo.query({
          query: QUERY_SINGLE_LOGIN,
          fetchPolicy: 'no-cache',
          variables: {
            authCode: this.$getAuthCode(),
          },
        })

        await this.setUserData(user, session_id)

        if (hash) this.redirectToSpecificRoute(hash)
        else this.redirectToFirstPermission(user.pages || [])
      } catch (error) {
        console.log(error)
        const singleLoginURL = this.$microservicesUrls['mfLogin']
        const host = location.href.slice(0, location.href.indexOf('#'))
        const hash = window.localStorage.getItem('redirectAfterLogin') || ''

        this.$logout()
        window.location.href = formattedRedirectLogout(singleLoginURL, host, hash)
      } finally {
        this.checkedToken = true
      }
    },
    getAuthCodeFromURL() {
      return window.location.hash.slice(window.location.hash.indexOf('?') + 1).split('=')[1]
    },
    getHost() {
      return location.href.slice(0, location.href.indexOf('#'))
    },
    async setUserData(user, session_id) {
      const fingerprint = await getFingerprint()
      this.$setFingerPrint(fingerprint)

      this.setUserPermissions(user.pages || [])
      if (user.multi_user) {
        this.$setIsMultiUser(true)
        this.$setSessionId(session_id)
      }

      this.$setSessionId(session_id)

      this.$setLoggedUser({
        name: user.name,
        username: user.login,
        mf: user.mf,
        admin: user.admin,
        client: user.account.name,
        profiles: user.profiles,
        id_external: user.account.id_external,
        id_account: user.account._id,
        id_user: user._id,
      })

      this.$setIsAdmin(user.mf)
      this.$setIsEcommerce(user.ecommerce)
      this.$setIsClub(user.club)

      this.$setAllowedModules(user.account.modules)
      this.$setPiiAccess(user.has_pii_access || false)
      this.$setPiiEmployeesAccess(user.has_pii_employees_access || false)

      // if (this.isFranchisee) {
      //   await this.getAccountFranchiseePermissions(user)
      //     .then(() => {
      //       const autoRedirectRoute = window.localStorage.getItem('redirectAfterLogin')
      //       if (autoRedirectRoute) this.redirectToSpecificRoute(autoRedirectRoute)
      //       else {
      //         this.$router.go('/home')
      //       }
      //     })
      //     .catch(() => {
      //       this.$router.go('/home')
      //     })
      // } else {
      //   const autoRedirectRoute = window.localStorage.getItem('redirectAfterLogin')
      //   if (autoRedirectRoute) this.redirectToSpecificRoute(autoRedirectRoute)
      //   else {
      //     this.$router.go('/home')
      //   }
      // }
    },
    verifyRefreshToken() {
      this.checkedToken = false
      return this.$apollo
        .query({
          query: QUERY_REFRESH,
          fetchPolicy: 'no-cache',
        })
        .then(({ data: { refreshToken } }) => {
          this.checkedToken = true
          if (!refreshToken.length) {
            this.$router.push('/expired-session')
          }
        })
        .catch(() => {
          this.checkedToken = true
          this.$router.push('/expired-session')
        })
    },
    redirectToSpecificRoute(route) {
      window.localStorage.removeItem('redirectAfterLogin')
      this.$router.push(route)
    },
    redirectToFirstPermission(permissions) {
      this.$router.push('/home')

      // const firstPermission = permissions.includes('home') ? 'home' : permissions[0]

      // const parent = this.$router.options.routes.find((route) => {
      //   if (route.meta && route.meta.pagePermission === firstPermission) return route
      //   else {
      //     if (route.children) {
      //       const children = route.children.find((children) => {
      //         if (children.meta && children.meta.pagePermission === firstPermission) return children
      //       })
      //       return children
      //     }
      //   }
      // })

      // if (parent.children) {
      //   const children = parent.children.find((children) => {
      //     return children.meta && children.meta.pagePermission === firstPermission
      //   })
      //   this.$router.push(`${parent.path}/${children.path}`)
      // } else {
      //   this.$router.push(`${parent.path}`).catch(() => {})
      // }
    },
    removeAuthCodeFromURL() {
      this.$router.replace({
        ...this.$router.currentRoute,
        query: {
          auth_code: undefined,
        },
      })
    },
  },
}
</script>

<style lang="scss">
.v-application {
  font-family: 'Lato', sans-serif !important;
}
h1,
h2,
h3,
h4,
h5,
p {
  color: $preto;
}
p {
  color: rgba(0, 0, 0, 0.86);
}
html {
  overflow: auto !important;
}
body {
  background-color: $cinza-l90 !important;
  -webkit-font-smoothing: antialiased;
}
.primary-text {
  opacity: 0.87 !important;
}
.secondary-text {
  opacity: 0.54 !important;
}
.mf-h1 {
  font-family: 'Lato', sans-serif;
  font-weight: lighter;
  font-size: 96px;
  letter-spacing: -2px;
}
.mf-h2 {
  font-family: 'Lato', sans-serif;
  font-weight: lighter;
  font-size: 72px;
  letter-spacing: -1px;
}
.mf-h3 {
  font-family: 'Lato', sans-serif;
  font-weight: normal;
  font-size: 56px;
  letter-spacing: 0;
}
.mf-h4 {
  font-family: 'Lato', sans-serif;
  font-weight: normal;
  font-size: 40px;
  letter-spacing: 0.4px;
}
.mf-h5 {
  font-family: 'Lato', sans-serif;
  font-weight: normal;
  font-size: 24px;
  letter-spacing: 0.4px;
}
.mf-h6 {
  font-family: 'Lato', sans-serif;
  font-weight: bold;
  font-size: 18px;
  letter-spacing: 0.4px;
}
.mf-subtitle-1 {
  font-family: 'Lato', sans-serif;
  font-weight: normal;
  font-size: 16px;
  letter-spacing: 0.2px;
}
.mf-subtitle-2 {
  font-family: 'Lato', sans-serif;
  font-weight: bold;
  font-size: 14px;
  letter-spacing: 0.2px;
}
.mf-body-1 {
  font-family: 'Lato', sans-serif;
  font-weight: normal;
  font-size: 16px;
  letter-spacing: 0.4px;
}
.mf-body-2 {
  font-family: 'Lato', sans-serif;
  font-weight: normal;
  font-size: 14px;
  letter-spacing: 0.4px;
}
.mf-button {
  font-family: 'Lato', sans-serif;
  font-weight: normal;
  font-size: 14px;
  letter-spacing: 0.4px;
}
.mf-caption {
  font-family: 'Lato', sans-serif;
  font-weight: normal;
  font-size: 12px;
  letter-spacing: 0.2px;
}
.mf-overline {
  font-family: 'Lato', sans-serif;
  font-weight: bold;
  font-size: 10px;
  letter-spacing: 1px;
}

.unset-capitalize {
  text-transform: unset !important;
  letter-spacing: 0.02px !important;
}

.user-select-none {
  user-select: none;
}
.v-application,
.base-page-layout {
  font-family: 'Lato', sans-serif;
  background-color: $cinza-l90 !important;
}
.fade-enter-active {
  transition: opacity 0.5s;
}

.fade-leave-active {
  opacity: 0;
}

.fade-enter {
  opacity: 0;
}

.fade-in-up-enter-active {
  transition: all 0.5s;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-in-up-leave-active {
  opacity: 0;
}

.fade-in-up-enter {
  opacity: 0;
  transform: translate3d(0, 100%, 0);
}
.overflow-x {
  overflow-x: auto;
}
.responsive-image {
  width: 100%;
  height: auto;
}
.ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.bold {
  font-weight: bold !important;
}
.child-view {
  position: absolute;
  transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}
.slide-up-enter {
  opacity: 0;
  -webkit-transform: translate(0, 30px);
  transform: translate(0, 30px);
}
.slide-up-leave-active {
  opacity: 0;
  -webkit-transform: translate(0, -30px);
  transform: translate(0, -30px);
}
td a {
  text-decoration: underline;
}
@media print {
  .v-toolbar {
    display: none;
  }
  .v-navigation-drawer {
    display: none;
  }
  .v-main {
    padding: 0 !important;
  }
}

.cursor-pointer {
  cursor: pointer;
}

.v-snack {
  z-index: 10000 !important;
}
.v-chip:not(.v-chip--active) {
  background: $cinza-l80 !important;
  color: $cinza-0 !important;
}
.v-card {
  box-shadow: $shadow-card !important;
}
</style>
